import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import NewsPage from './pages/NewsPage';
import DevicesPage from './pages/DevicesPage';
import ParticleDevicesPage from './pages/ParticleDevicesPage';
import WifiSetupPage from './pages/WifiSetupPage';
import DevicePage from './pages/DevicePage';
import WaterDevicePage from './pages/WaterDevicePage';
import CuttingsPage from './pages/CuttingsPage';
import CuttingsAdminPage from './pages/CuttingsAdminPage';
import StrainsAdminPage from './pages/StrainsAdminPage';
import StrainsPage from './pages/StrainsPage';
import StrainDetailPage from './pages/StrainDetailPage';
import ReviewsAdminPage from './pages/ReviewsAdminPage';
import CuttingDetailPage from './pages/CuttingDetailPage';
import NewsAdminPage from './pages/NewsAdminPage';
import LoginPage from './pages/LoginPage';
import InviteAcceptPage from './pages/InviteAcceptPage';
import UsersPage from './pages/UsersPage';
import FarmsAdminPage from './pages/FarmsAdminPage';
import RoomPage from './pages/RoomPage';
import UnassignedDevicesPage from './pages/UnassignedDevicesPage';
import GlobalControllersPage from './pages/GlobalControllersPage';
import BugsPage from './pages/BugsPage';
import TasksPage from './pages/TasksPage';
import HomePage from './pages/HomePage';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import TelegramConnectPage from './pages/TelegramConnectPage';
import RateLimitedPage from './pages/RateLimitedPage';
import { useLang } from './LanguageContext';
import MainLayout from './components/MainLayout';
import ShefaAppBoot from './components/ShefaAppBoot';
import FarmDashboardRedirect from './components/FarmDashboardRedirect';
import { AuthProvider, useAuth } from './context/AuthContext';
import { FarmProvider, useFarm } from './context/FarmContext';
import { isGlobalRole, isFarmLeadRole, isWorkerRole, normalizeRole } from './utils/roles';
import { getFarmOverviewPath } from './utils/farmRoutes';

function PrivateRoute({ children }) {
  const { token, ready } = useAuth();
  const location = useLocation();
  if (!ready) return null;
  if (!token) return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}${location.hash}` }} />;
  return children;
}

function FarmAdminRoute({ children }) {
  const { token, user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return null;
  if (!token) return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}${location.hash}` }} />;
  const role = normalizeRole(user?.role);
  if (!isGlobalRole(role) && !isFarmLeadRole(role)) return <Navigate to="/" replace />;
  return children;
}

function SuperuserRoute({ children }) {
  const { token, user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return null;
  if (!token) {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}${location.hash}` }} />;
  }
  if (!isGlobalRole(user?.role)) return <Navigate to="/" replace />;
  return children;
}

function RoleGate({ children, allow }) {
  const { user } = useAuth();
  const role = normalizeRole(user?.role);
  if (Array.isArray(allow) && !allow.includes(role)) return <Navigate to="/" replace />;
  return children;
}

function WorkerLimitedRoute({ children }) {
  const { token, user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return null;
  if (!token) return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}${location.hash}` }} />;
  if (isWorkerRole(user?.role)) {
    const path = location.pathname || '';
    const isFarmOverview = /^\/farms\/[^/]+\/overview$/.test(path);
    const workerAllowed =
      isFarmOverview ||
      path === '/dashboard' ||
      path === '/tasks' ||
      path === '/profile' ||
      path.startsWith('/rooms/');
    if (!workerAllowed) {
      const target = getFarmOverviewPath(user?.farmId);
      if (target) return <Navigate to={target} replace />;
      return <Navigate to="/" replace />;
    }
  }
  return children;
}

function DeviceSettingsTabRedirect() {
  const { id } = useParams();
  return <Navigate to={`/device/${encodeURIComponent(id)}?tab=settings`} replace />;
}

/** Old list URL `/rooms` — send users to the farm overview dashboard. */
function RoomsListRedirect() {
  const { user } = useAuth();
  const { effectiveFarmId } = useFarm();
  const target = getFarmOverviewPath(effectiveFarmId || user?.farmId);
  if (target) return <Navigate to={target} replace />;
  return <Navigate to="/" replace />;
}

function AppRoutes() {
  const { ready } = useAuth();

  if (!ready) {
    return null;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/rate-limited" element={<RateLimitedPage />} />
      <Route path="/news" element={<NewsPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/invite" element={<InviteAcceptPage />} />
      <Route path="/telegram/connect" element={<TelegramConnectPage />} />

      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <WorkerLimitedRoute>
              <FarmDashboardRedirect />
            </WorkerLimitedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/farms/:farmId/overview"
        element={
          <PrivateRoute>
            <WorkerLimitedRoute>
              <DashboardPage />
            </WorkerLimitedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <PrivateRoute>
            <WorkerLimitedRoute>
              <ProfilePage />
            </WorkerLimitedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/devices"
        element={
          <PrivateRoute>
            <WorkerLimitedRoute>
              <DevicesPage />
            </WorkerLimitedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/devices/particle"
        element={
          <PrivateRoute>
            <RoleGate allow={['admin', 'superuser', 'farm_owner', 'farm_manager']}>
              <ParticleDevicesPage />
            </RoleGate>
          </PrivateRoute>
        }
      />
      <Route
        path="/devices/wifi-setup"
        element={
          <PrivateRoute>
            <RoleGate allow={['admin', 'superuser', 'farm_owner']}>
              <WifiSetupPage />
            </RoleGate>
          </PrivateRoute>
        }
      />
      <Route
        path="/strains"
        element={
          <PrivateRoute>
            <RoleGate allow={['admin', 'superuser', 'farm_owner', 'farm_manager']}>
              <StrainsPage />
            </RoleGate>
          </PrivateRoute>
        }
      />
      <Route
        path="/strain/:slug"
        element={
          <PrivateRoute>
            <RoleGate allow={['admin', 'superuser', 'farm_owner', 'farm_manager']}>
              <StrainDetailPage />
            </RoleGate>
          </PrivateRoute>
        }
      />
      <Route
        path="/bugs"
        element={
          <PrivateRoute>
            <RoleGate allow={['admin', 'superuser', 'farm_owner', 'farm_manager']}>
              <BugsPage />
            </RoleGate>
          </PrivateRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <PrivateRoute>
            <WorkerLimitedRoute>
              <TasksPage />
            </WorkerLimitedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/cuttings"
        element={
          <PrivateRoute>
            <RoleGate allow={['admin', 'superuser', 'farm_owner', 'farm_manager']}>
              <CuttingsPage />
            </RoleGate>
          </PrivateRoute>
        }
      />
      <Route
        path="/cutting/:id"
        element={
          <PrivateRoute>
            <RoleGate allow={['admin', 'superuser', 'farm_owner', 'farm_manager']}>
              <CuttingDetailPage />
            </RoleGate>
          </PrivateRoute>
        }
      />
      <Route
        path="/cuttings-admin"
        element={
          <PrivateRoute>
            <RoleGate allow={['admin', 'superuser', 'farm_owner', 'farm_manager']}>
              <CuttingsAdminPage />
            </RoleGate>
          </PrivateRoute>
        }
      />
      <Route
        path="/strains-admin"
        element={
          <PrivateRoute>
            <RoleGate allow={['admin', 'superuser', 'farm_owner', 'farm_manager']}>
              <StrainsAdminPage />
            </RoleGate>
          </PrivateRoute>
        }
      />
      <Route
        path="/reviews-admin"
        element={
          <PrivateRoute>
            <RoleGate allow={['admin', 'superuser']}>
              <ReviewsAdminPage />
            </RoleGate>
          </PrivateRoute>
        }
      />
      <Route
        path="/news-admin"
        element={
          <PrivateRoute>
            <RoleGate allow={['admin', 'superuser', 'farm_owner', 'farm_manager']}>
              <NewsAdminPage />
            </RoleGate>
          </PrivateRoute>
        }
      />
      <Route
        path="/device/:id"
        element={
          <PrivateRoute>
            <RoleGate allow={['admin', 'superuser', 'farm_owner', 'farm_manager']}>
              <DevicePage />
            </RoleGate>
          </PrivateRoute>
        }
      />
      <Route
        path="/device/:id/settings"
        element={
          <PrivateRoute>
            <RoleGate allow={['admin', 'superuser', 'farm_owner', 'farm_manager']}>
              <DeviceSettingsTabRedirect />
            </RoleGate>
          </PrivateRoute>
        }
      />
      <Route
        path="/devices/water/:deviceId"
        element={
          <PrivateRoute>
            <RoleGate allow={['admin', 'superuser', 'farm_owner', 'farm_manager']}>
              <WaterDevicePage />
            </RoleGate>
          </PrivateRoute>
        }
      />
      <Route
        path="/users"
        element={
          <PrivateRoute>
            <RoleGate allow={['admin', 'superuser', 'farm_owner', 'farm_manager']}>
              <UsersPage />
            </RoleGate>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/farms"
        element={
          <FarmAdminRoute>
            <FarmsAdminPage />
          </FarmAdminRoute>
        }
      />
      <Route
        path="/admin/farms/:id"
        element={
          <FarmAdminRoute>
            <FarmsAdminPage />
          </FarmAdminRoute>
        }
      />
      <Route
        path="/rooms"
        element={
          <PrivateRoute>
            <WorkerLimitedRoute>
              <RoomsListRedirect />
            </WorkerLimitedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/rooms/:roomId"
        element={
          <PrivateRoute>
            <WorkerLimitedRoute>
              <RoomPage />
            </WorkerLimitedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/unassigned-devices"
        element={
          <SuperuserRoute>
            <UnassignedDevicesPage />
          </SuperuserRoute>
        }
      />
      <Route
        path="/admin/controllers"
        element={
          <PrivateRoute>
            <RoleGate allow={['admin', 'superuser']}>
              <GlobalControllersPage />
            </RoleGate>
          </PrivateRoute>
        }
      />
    </Routes>
  );
}

export default function App() {
  const { lang } = useLang();
  const dir = lang === 'he' ? 'rtl' : 'ltr';
  const align = lang === 'he' ? 'right' : 'left';

  return (
    <div className="shefa-app-root" dir={dir} style={{ textAlign: align, overflowX: 'hidden', maxWidth: '100%' }}>
      <Router>
        <AuthProvider>
          <FarmProvider>
            <ShefaAppBoot>
              <MainLayout>
                <AppRoutes />
              </MainLayout>
            </ShefaAppBoot>
          </FarmProvider>
        </AuthProvider>
      </Router>
    </div>
  );
}
